import React, { useState, useEffect } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import FlightSearchCard from "../components/Flights/FlightSearchCard";
import { apiRoutes, routes } from "../routes/routes";
import MarkdownViewer from "../components/MarkdownViewer";
import api from "../api";
import {
  FaCommentDots,
  FaMagnifyingGlass,
  FaMapLocationDot,
  FaPaperPlane,
} from "react-icons/fa6";
import LoadingMessage from "../components/LoadingMessage";
import ItineraryChatMessage from "../components/Itinerary/ItineraryChatMessage";
import {
  Message,
  ChatActionType,
  Itinerary,
  TransportItem,
  FlightSearchParams,
  TransportSearchParams,
  Conversation,
  SearchResponse,
} from "../types";
import { useConversationsContext } from "../context/ConversationsListContext";
import axios, { AxiosError } from "axios";
import ChatHeader from "../components/ChatHeader";
import ChatActions from "../components/ChatActions";
import LayoutWrapper from "../layouts/LayoutWrapper";
import SuggestionBubbles from "../components/SuggestionBubbles";
import TravelSearchForm from "../components/searchForm/TravelSearchForm";
import {
  getTransportIcon,
  llmTextSeparator,
} from "../functions/ItineraryUtils";
import CarRentalsSearchCard from "../components/CarRentals/CarRentalsSearchCard";
import HotelSearchCard from "../components/Hotels/HotelSearchCard";
import TransportSearchCard from "../components/Transports/TransportSearchCard";
import TransportSearchResults from "../components/Transports/TransportSearchResults";
import FlightCard from "../components/Flights/FlightCard";
import TransportCard from "../components/Transports/TransportCard";
import { Card } from "react-bootstrap";
import { checkImageExists, formatDate } from "../functions/Utils";

type ChatResponse = {
  message: string;
  // intent: 'gen_itinerary' | 'flight_search' | 'hotel_search' | 'other';
  intent?: string;
  complete?: boolean;
  itinerary?: any;
  missing_fields?: string[];
  parsed?: any;
  title?: any;
};

const ChatInterface: React.FC = () => {
  const [conversationId, setConversationId] = useState("");
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [searchResponse, setSearchResponse] = useState<SearchResponse[]>([]);
  const [searchResultsType, setSearchResultsType] = useState<string | null>(
    null
  );
  const [searchResultsParams, setSearchResultsTypeParams] = useState<
    TransportSearchParams | FlightSearchParams | null
  >(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { loadUserConversations, addUserConversation } =
    useConversationsContext();
  const [selectedAction, setSelectedAction] = useState<string>(
    ChatActionType.CHAT
  );
  const [backgroundImageUrl, setBackgroundImageUrl] =
    useState<string>("/images/lisboa.jpg");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const formatMessage = (content: string) => {
    return content.split(llmTextSeparator)[0];
  };

  const createMessage = async (message: Message, conservationId: string) => {
    message.conversation_uuid = conservationId;

    // create a copy to avoid modifying the values for render (itinerary_generation)
    const _message = { ...message };

    if (
      message.role == "assistant" &&
      message.intent == "generate_itinerary"
    ) {
      _message.parsed = null;
      message.parsed.is_new = true;
    }

    const res = await api.post(apiRoutes.messages, _message, {
      headers: { "Content-Type": "application/json" },
    });

    message.uuid = res.data.uuid;
  };

  const openSearchResults = (
    type: string,
    response: SearchResponse[],
    params: TransportSearchParams | FlightSearchParams
  ) => {
    setSearchResponse(response);
    setSearchResultsType(type);
    setSearchResultsTypeParams(params);
  };

  const closeSearchResults = () => {
    setSearchResponse([]);
    setSearchResultsType(null);
    setSearchResultsTypeParams(null);
  };

  const createConversation = async (message: string) => {
    let title = "";
    if (message.length < 10) {
      title = "New Conversation";
    } else {
      title = message.trim().slice(0, 80);
    }

    // try{
    //   const response: any = await api.post(
    //     apiRoutes.get_conversation_title,
    //     { message: message },
    //     {
    //       headers: { "Content-Type": "application/json" },
    //     }
    //   );
    //   title = response.data.conversation_title;
    // } catch (err){

    // }

    document.title = title;

    const response: any = await api.post(
      apiRoutes.conversations,
      { title: title },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    setConversationId(response.data.uuid);
    setConversation(response.data);
    // loadUserConversations();
    // addUserConversation(response.data);

    return response.data ? response.data : null;
  };

  const updateConversation = async (conversation_uuid: string, data: any) => {
    try {
      const response = await api.patch(
        apiRoutes.conversation_details.replace(":uuid", conversation_uuid),
        data
      );
    } catch (err) {
      console.error("Error updating conversation:", err);
      throw err; // Re-throw the error to be handled by the caller
    }
  };

  const handleselectedAction = (value: string) => {
    setSelectedAction(value);
    if (value === ChatActionType.ITINERARY && itinerary) {
      navigate(`${routes.itinerary.path}/${itinerary.uuid}`);
      return false;
    }

    const actionParams = searchParams.get("action");
    if (value === ChatActionType.CHAT) {
      searchParams.delete("action");
    }
    if (!actionParams && value === ChatActionType.SEARCH) {
      searchParams.append("action", ChatActionType.SEARCH);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleSuggestionClick = (text: string) => {
    setInput(text);
    handleSend(text);
  };

  useEffect(() => {
    const actionParams = searchParams.get("action");
    if (actionParams && actionParams != ChatActionType.ITINERARY) {
      handleselectedAction(actionParams);
    }

    const loadConversation = async () => {
      setMessages([]);
      setConversationId("");
      setItinerary(null);
      setConversation(null);
      closeSearchResults();
      document.title = "New Chat";

      if (!uuid) return;

      setConversationId(uuid);

      setLoadingConversation(true);
      setError(null);

      try {
        const response = await api.get(
          apiRoutes.conversation_details.replace(":uuid", uuid)
        );

        if (response.status == 200) {
          setConversation(response.data);

          document.title = response.data.title;

          const response_messages = response.data.messages.sort(
            (a: any, b: any) => a.timestamp.localeCompare(b.timestamp)
          );
          setMessages(response_messages);

          response_messages.forEach((message: Message, index: number) => {
            if (message.itineraries?.length) {
              setItinerary(message.itineraries[0]);
              return;
            } else {
            }
          });
        } else if (response.status == 404) {
          navigate(routes.chat.path);
        }
      } catch (err) {
        const error = err as AxiosError;
        if (error.response?.status == 404) {
          navigate(routes.chat.path);
        }
        setError("Impossible de charger les messages.");
      } finally {
        setLoadingConversation(false);
      }
    };

    loadConversation();
  }, [uuid]); // important pour relancer si l'UUID change

  const handleSend = async (value?: string) => {
    const inputMessage = value ?? input;
    if (!inputMessage.trim()) return;

    const newUserMessage = {
      role: "user",
      content: inputMessage,
      intent: null,
      timestamp: Date.now(),
    };

    let conversation_uuid = conversationId;
    let conversationObj: Conversation | null = null;

    if (conversation_uuid == "" && messages.length == 0) {
      conversationObj = await createConversation(inputMessage);

      if (conversationObj) {
        conversation_uuid = conversationObj.uuid;
        setConversation(conversationObj);
      }

      if (!uuid && !conversationId) {
        navigate(`/chat/${conversation_uuid}`, { replace: false });
      }
    }

    const updatedMessages: any = [...messages, newUserMessage];

    await createMessage(newUserMessage, conversation_uuid);
    setMessages(updatedMessages);
    setInput("");

    setLoading(true);

    try {
      const response = await api.post(
        apiRoutes.chat,
        { messages: updatedMessages },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const data: ChatResponse = response.data;
      // set intent to previous user input message
      // updateLastUserIntent(data.intent);

      const assistantMessage = {
        role: "assistant",
        content: data.message,
        timestamp: Date.now(),
        parsed: data.parsed,
        intent: data.intent,
      };

      //only on first answer
      if (data.title) {
        try {
          const response_conversation_title: any = await api.post(
            apiRoutes.get_conversation_title,
            { message: inputMessage },
            {
              headers: { "Content-Type": "application/json" },
            }
          );

          await updateConversation(conversation_uuid, {
            title: response_conversation_title.data.name,
            data: response_conversation_title.data,
          });

          if (conversationObj) {
            const newConversation = {
              ...conversationObj,
              ...{
                title: response_conversation_title.data.name,
                data: response_conversation_title.data,
              },
            };

            setConversation(newConversation);
            //update conversations menu list
            addUserConversation(newConversation);
          }
        } catch (err) {}
      }

      await createMessage(assistantMessage, conversation_uuid);
      setMessages((prev) => [...prev, assistantMessage]);

      // if (data.intent === 'gen_itinerary' && data.complete && data.itinerary) {
      //   navigate('/results', { state: { itinerary: data.itinerary } });
      // }
    } catch (err) {
      console.error("Erreur API :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchBackgroundImageUrl = async () => {
      let backgroundUrl = "/images/lisboa.jpg"; // fallback par défaut

      if (itinerary?.data?.images?.length) {
        backgroundUrl = itinerary.data.images[0].url;
      } else if (conversation?.data?.country_code) {
        const image_path = `/images/countries/${conversation.data.country_code.toLocaleLowerCase()}.jpg`;
        const imageExists = await checkImageExists(image_path);
        if (imageExists) {
          backgroundUrl = image_path;
        }
      }

      if (backgroundUrl !== backgroundImageUrl) {
        setBackgroundImageUrl(backgroundUrl);
      }
    };

    fetchBackgroundImageUrl();
  }, [conversation, itinerary, backgroundImageUrl]);

  return (
    <div>
      <LayoutWrapper
        header={conversation ? <ChatHeader conversation={conversation} /> : ""}
        left={
          <>
            {messages && conversation && (
              <ChatActions
                conversation={conversation}
                action={selectedAction}
                itinerary={itinerary}
                handleselectedAction={handleselectedAction}
              />
            )}

            {selectedAction == ChatActionType.CHAT && (
              <div>
                <div className="bg-light">
                  <div className="container p-0">
                    <div className="chat-box border-0 pt-3 mb-3">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`mb-2 px-3 px-lg-5 d-flex ${
                            msg.role === "user"
                              ? "text-start justify-content-around"
                              : "text-start justify-content-around"
                          } `}
                        >
                          <div
                            style={{ maxWidth: "750px" }}
                            className={`w-100 my-2 rounded-4 p-3 border-start border-5 bg-white message-content ${
                              msg.role === "user"
                                ? "border-primary"
                                : "border-secondary mb-5"
                            }`}
                          >
                            {(!msg.intent ||
                              (msg?.intent &&
                                (msg?.intent.includes("_information") ||
                                  ["not_travel_related"].includes(
                                    msg?.intent
                                  )))) && (
                              <>
                                <MarkdownViewer content={formatMessage(msg.content)} />
                                

                                  {msg?.parsed && msg.parsed.map(
                                    (tool: any, _id: number) => (
                                      <div key={_id}>


                                        {tool.name == "search_flights" && (
                                          <FlightSearchCard
                                            flightParams={tool.arguments}
                                            openSearchResults={
                                              openSearchResults
                                            }
                                          />
                                        )}



                                        {tool.name == "search_ground_transport" && (
                                          <TransportSearchCard
                                            transportParams={tool.arguments}
                                            message={msg}
                                            openSearchResults={openSearchResults}
                                          />
                                        )}


                                        {tool.name == "search_hotel" && (
                                          <HotelSearchCard
                                            params={tool.arguments}
                                          />
                                        )}


                                        {tool.name == "search_car_rental" && (
                                          <CarRentalsSearchCard
                                            params={tool.arguments}
                                          />
                                        )}


                                        {tool.name == "generate_itinerary" && (
                                          <ItineraryChatMessage message={msg} params={tool.arguments}/>
                                        )}


                                      </div>
                                    )
                                  )}

                              </>
                            )}

                            {/* {msg?.intent == "flight_tickets" && msg?.parsed && (
                              <div>
                                <MarkdownViewer content={msg.content} />
                                <FlightSearchCard
                                  flightParams={msg.parsed}
                                  openSearchResults={openSearchResults}
                                />
                              </div>
                            )}

                            {msg?.intent &&
                              [
                                "ferry_tickets",
                                "train_tickets",
                                "bus_tickets",
                                "carpooling_offers",
                              ].includes(msg.intent) &&
                              msg?.parsed && (
                                <div>
                                  <MarkdownViewer content={msg.content} />
                                  <TransportSearchCard
                                    transportParams={msg.parsed}
                                    message={msg}
                                    openSearchResults={openSearchResults}
                                  />
                                </div>
                              )}

                            {msg?.intent == "itinerary_generation" && (
                              <div>
                                <MarkdownViewer
                                  content={formatMessage(msg.content)}
                                />
                                <ItineraryChatMessage message={msg} />
                              </div>
                            )}

                            {msg?.intent == "hotel_booking" && (
                              <div>
                                <MarkdownViewer content={msg.content} />
                                <HotelSearchCard params={msg.parsed} />
                              </div>
                            )}

                            {msg?.intent &&
                              [
                                "car_rentals_search",
                                "car_rentals_information",
                              ].includes(msg.intent) && (
                                <div>
                                  <MarkdownViewer content={msg.content} />
                                  {msg.intent == "car_rentals_search" && (
                                    <CarRentalsSearchCard params={msg.parsed} />
                                  )}
                                </div>
                              )} */}
                          </div>
                        </div>
                      ))}
                      {(loading || loadingConversation) && (
                        <div className="d-flex justify-content-around">
                          <div style={{ maxWidth: "75%" }} className="w-100">
                            <LoadingMessage />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="container p-5">
                  {messages.length == 0 && (
                    <div className="mb-4">
                      <SuggestionBubbles onSelect={handleSuggestionClick} />
                    </div>
                  )}
                  <div className="input-group border rounded-4 p-2 shadow">
                    <textarea
                      // type="text"
                      className="form-control p-3 border-0"
                      placeholder="Posez votre question ou proposez une destination..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                      disabled={loading}
                    />
                    <button
                      style={{ height: "45px", width: "45px" }}
                      className="btn btn-primary rounded-circle d-flex align-items-center"
                      onClick={() => handleSend()}
                      disabled={loading}
                    >
                      <FaPaperPlane />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {selectedAction === ChatActionType.ITINERARY && (
              <div>Itinerary</div>
            )}

            {/* {selectedAction === ChatActionType.SEARCH && (
              <TravelSearchForm  ="ferry" />
            )} */}
          </>
        }
        right={
          selectedAction === ChatActionType.CHAT && searchResponse.length ? (
            <div>
              <div className="d-flex justify-content-between p-2 bg-info text-white">
                <div>
                  <div className="d-flex align-items-center">
                    {/* Picto à gauche */}
                    <div className="me-3">
                      {searchResultsType
                        ? getTransportIcon(searchResultsType, 24)
                        : ""}
                    </div>
                    {/* Texte à droite */}

                    {searchResultsParams && (
                      <div>
                        <div className="fw-bold">
                          {searchResultsParams.origin}{" "}
                          {searchResultsParams.destination}
                        </div>
                        <div className="">
                          {formatDate(searchResultsParams.departure_date)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSearchResponse([])}
                />
              </div>

              <div className="px-4 position-relative">
                <TransportSearchResults
                  responses={searchResponse}
                  renderResult={(searchResults) =>
                    searchResultsType == "flight" ? (
                      <FlightCard flight={searchResults} />
                    ) : (
                      <TransportCard offer={searchResults} />
                    )
                  }
                  completed={1}
                  transport_mode_str={searchResultsType}
                />
              </div>
            </div>
          ) : (
            <div
              className="login-cover h-100 w-100"
              style={{
                backgroundImage: `url(${backgroundImageUrl})`,
                // maxHeight: "100vh",
              }}
            />
          )
        }
      />
    </div>
  );
};

export default ChatInterface;
